import { useState, useEffect } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronDown, Heart, Shield, Award, Camera,
  GraduationCap, TreePine, Info, FileText, Crown, Dna,
  MapPin, Calendar, User, Fingerprint, Palette, ArrowRight, X, ChevronLeft, ChevronRight
} from "lucide-react";
import KennelSEO from "./KennelSEO";

const C = { bg: "#0B0B0D", sec: "#141416", card: "#1A1A1D", gold: "#D4AF37", goldS: "#C8A96A", txt: "#F5F5F5", sub: "#B8B8B8", brd: "#2A2A2D" };

const TABS = [
  { key: "rodowod", label: "Rodowód", icon: TreePine },
  { key: "info", label: "Info", icon: Info },
  { key: "zdrowie", label: "Zdrowie", icon: Shield },
  { key: "szkolenie", label: "Szkolenie", icon: GraduationCap },
  { key: "wystawy", label: "Wystawy", icon: Award },
  { key: "certyfikaty", label: "Certyfikaty", icon: FileText },
  { key: "galeria", label: "Galeria", icon: Camera },
];

/* ── Pedigree ── */
const PedigreeCell = ({ dog, gen = 0 }) => {
  if (!dog?.name || dog.name === "(do uzupelnienia)") return (
    <div className="h-full border border-dashed border-[#2A2A2D] bg-[#141416]/50 flex items-center justify-center px-2 py-2 min-h-[48px]">
      <span className="text-[10px] text-[#B8B8B8]/20 italic">—</span>
    </div>
  );
  const bgs = [
    `bg-[${C.card}] border-[${C.gold}]/20`,
    `bg-[${C.sec}] border-[${C.brd}]`,
    `bg-[${C.bg}] border-[${C.brd}]/70`,
    `bg-[${C.bg}] border-[${C.brd}]/40`,
  ];
  return (
    <div className={`h-full border border-[#2A2A2D] bg-[#1A1A1D] px-3 py-2 min-h-[48px] flex flex-col justify-center ${gen === 0 ? "border-[#D4AF37]/20" : ""}`}>
      {dog.titles && (
        <div className="flex gap-1 mb-1 flex-wrap">
          {dog.titles.split(",").map((t, i) => (
            <span key={i} className="text-[8px] px-1 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] font-medium leading-none">{t.trim()}</span>
          ))}
        </div>
      )}
      <p className={`font-medium leading-tight ${gen === 0 ? "text-xs text-[#F5F5F5]" : "text-[11px] text-[#B8B8B8]"}`}>{dog.name}</p>
      {dog.country && <p className="text-[9px] text-[#B8B8B8]/40 mt-0.5">{dog.country}</p>}
    </div>
  );
};

const PedigreeTree = ({ pedigree }) => {
  if (!pedigree) return <div className="text-center py-12"><Dna className="w-8 h-8 text-[#B8B8B8]/20 mx-auto mb-3" /><p className="text-[#B8B8B8]/30 text-sm">Rodowód w przygotowaniu</p></div>;
  const g = (k) => pedigree[k] || {};
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <div className="min-w-[800px]">
        <div className="grid grid-cols-4 gap-2 mb-3">
          {["RODZICE", "DZIADKOWIE", "PRADZIADKOWIE", "GEN. IV"].map((h, i) => (
            <div key={h} className={`text-[10px] tracking-[0.15em] font-medium px-1 ${i === 0 ? "text-[#D4AF37]" : "text-[#B8B8B8]/40"}`}>{h}</div>
          ))}
        </div>
        {/* Father branch */}
        <div className="mb-3"><div className="grid grid-cols-4 gap-2" style={{ gridTemplateRows: "repeat(4,1fr)" }}>
          <div className="row-span-4 flex flex-col justify-center"><div className="text-[9px] text-[#D4AF37] tracking-wider mb-1 px-1 font-medium flex items-center gap-1"><Crown className="w-3 h-3" /> OJCIEC</div><PedigreeCell dog={g("father")} gen={0} /></div>
          <div className="row-span-2"><PedigreeCell dog={g("father_father")} gen={1} /></div>
          <div><PedigreeCell dog={g("father_father_father")} gen={2} /></div>
          <div><PedigreeCell dog={g("ffff")} gen={3} /></div>
          <div><PedigreeCell dog={g("father_father_mother")} gen={2} /></div>
          <div><PedigreeCell dog={g("fffm")} gen={3} /></div>
          <div className="row-span-2"><PedigreeCell dog={g("father_mother")} gen={1} /></div>
          <div><PedigreeCell dog={g("father_mother_father")} gen={2} /></div>
          <div><PedigreeCell dog={g("fmff")} gen={3} /></div>
          <div><PedigreeCell dog={g("father_mother_mother")} gen={2} /></div>
          <div><PedigreeCell dog={g("fmmf")} gen={3} /></div>
        </div></div>
        <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" /></div>
        {/* Mother branch */}
        <div><div className="grid grid-cols-4 gap-2" style={{ gridTemplateRows: "repeat(4,1fr)" }}>
          <div className="row-span-4 flex flex-col justify-center"><div className="text-[9px] text-[#D4AF37] tracking-wider mb-1 px-1 font-medium flex items-center gap-1"><Heart className="w-3 h-3" /> MATKA</div><PedigreeCell dog={g("mother")} gen={0} /></div>
          <div className="row-span-2"><PedigreeCell dog={g("mother_father")} gen={1} /></div>
          <div><PedigreeCell dog={g("mother_father_father")} gen={2} /></div>
          <div><PedigreeCell dog={g("mfff")} gen={3} /></div>
          <div><PedigreeCell dog={g("mother_father_mother")} gen={2} /></div>
          <div><PedigreeCell dog={g("mffm")} gen={3} /></div>
          <div className="row-span-2"><PedigreeCell dog={g("mother_mother")} gen={1} /></div>
          <div><PedigreeCell dog={g("mother_mother_father")} gen={2} /></div>
          <div><PedigreeCell dog={g("mmff")} gen={3} /></div>
          <div><PedigreeCell dog={g("mother_mother_mother")} gen={2} /></div>
          <div><PedigreeCell dog={g("mmmf")} gen={3} /></div>
        </div></div>
      </div>
    </div>
  );
};

/* ── Stat Badge ── */
const StatBadge = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <div className="w-9 h-9 bg-[#141416] border border-[#2A2A2D] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#C8A96A]" />
    </div>
    <div>
      <p className="text-[10px] text-[#B8B8B8]/40 tracking-wider uppercase">{label}</p>
      <p className="text-sm text-[#F5F5F5] font-medium">{value || "—"}</p>
    </div>
  </div>
);

/* ── Health badges ── */
const HealthBadge = ({ label, value }) => (
  <span className="inline-flex items-center gap-1 text-[10px] px-3 py-1.5 border border-[#D4AF37]/20 text-[#D4AF37] font-medium tracking-wider">
    {label}: {value}
  </span>
);

const KennelDogProfile = () => {
  const { dogId } = useParams();
  const { API } = useOutletContext();
  const [dog, setDog] = useState(null);
  const [allDogs, setAllDogs] = useState([]);
  const [tab, setTab] = useState("rodowod");
  const [dropdown, setDropdown] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [activeThumb, setActiveThumb] = useState(-1);

  useEffect(() => {
    fetch(`${API}/dogs/${dogId}`).then(r => r.json()).then(setDog);
    fetch(`${API}/dogs`).then(r => r.json()).then(setAllDogs);
  }, [API, dogId]);

  useEffect(() => { setTab("rodowod"); setDropdown(false); setActiveThumb(-1); setFailedImages(new Set()); }, [dogId]);
  const [failedImages, setFailedImages] = useState(new Set());

  const dogLbNav = (dir) => {
    const list = (dog?.gallery || []).filter(Boolean).filter(img => !failedImages.has(img));
    if (!list.length) return;
    setLightboxImg(cur => {
      let idx = list.indexOf(cur);
      if (idx === -1) idx = 0;
      const n = dir === "next" ? (idx + 1) % list.length : (idx - 1 + list.length) % list.length;
      return list[n];
    });
  };
  useEffect(() => {
    if (!lightboxImg) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxImg(null);
      else if (e.key === "ArrowLeft") dogLbNav("prev");
      else if (e.key === "ArrowRight") dogLbNav("next");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxImg, dog, failedImages]);

  if (!dog) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0D]">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const galleryPhotos = (dog.gallery || []).filter(Boolean).filter(img => !failedImages.has(img)).slice(0, 4);
  const allGalleryPhotos = (dog.gallery || []).filter(Boolean).filter(img => !failedImages.has(img));
  const profileImg = dog.image_url || null;
  const mainDisplayImg = activeThumb >= 0 && galleryPhotos[activeThumb] ? galleryPhotos[activeThumb] : profileImg;

  return (
    <div className="bg-[#0B0B0D] min-h-screen">
      <KennelSEO
        title={`${dog.name} | Wolfe vom Katzenberg — Owczarek Niemiecki`}
        description={`${dog.name} — owczarek niemiecki z hodowli Wolfe vom Katzenberg. ${dog.titles || ''} Rodowod, badania, certyfikaty i galeria.`}
        canonical={`https://wolfevomkatzenberg.pl/psy/${dog.id}`}
        ogImage={dog.image_url ? `https://wolfevomkatzenberg.pl${dog.image_url}` : undefined}
      />
      {/* Back + Selector */}
      <div className="bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 py-4 relative">
        <div className="absolute top-0 right-0 w-[400px] h-[200px] bg-[#D4AF37]/[0.03] rounded-full blur-[80px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 40px)" }} />
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/psy" data-testid="back-to-dogs" className="text-xs text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Nasze psy
          </Link>
          <div className="relative">
            <button onClick={() => setDropdown(!dropdown)} data-testid="dog-selector-dropdown"
              className="flex items-center gap-2 text-sm bg-[#1A1A1D] border border-[#2A2A2D] text-[#F5F5F5] px-4 py-2 hover:border-[#D4AF37]/30 transition-all"
            >
              <span>{dog.name}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-[#B8B8B8]/40 transition-transform ${dropdown ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {dropdown && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 top-full mt-2 bg-[#1A1A1D] border border-[#2A2A2D] min-w-[240px] z-50 shadow-xl overflow-hidden"
                >
                  {allDogs.map(d => (
                    <Link key={d.id} to={`/psy/${d.id}`} onClick={() => setDropdown(false)}
                      className={`block px-5 py-3.5 text-sm hover:bg-[#141416] transition-colors border-b border-[#2A2A2D] last:border-0 ${d.id === dog.id ? "bg-[#141416] text-[#D4AF37]" : "text-[#B8B8B8]"}`}
                    >
                      <span className="font-medium">{d.name}</span>
                      <span className="text-[10px] text-[#B8B8B8]/40 block mt-0.5">{d.registered_name}</span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 max-w-7xl mx-auto py-10">
        {/* ═══ HERO: Photo + Info ═══ */}
        <div className="grid lg:grid-cols-[420px_1fr] gap-8 mb-12">
          {/* Photo + Thumbnails */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Main image */}
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] overflow-hidden">
              <div className="overflow-hidden relative">
                {mainDisplayImg ? (
                  <img src={mainDisplayImg} alt={dog.name} data-testid="dog-main-image"
                    className="w-full h-auto max-h-[600px] object-contain cursor-pointer hover:scale-[1.03] transition-transform duration-500"
                    onClick={() => setLightboxImg(mainDisplayImg)}
                    onError={() => setFailedImages(prev => new Set([...prev, mainDisplayImg]))}
                  />
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center bg-[#141416] text-[#B8B8B8]/10 text-8xl font-serif">{dog.name[0]}</div>
                )}
                <div className="absolute top-4 right-4 flex flex-col gap-1.5">
                  {dog.health?.filter(h => h.status === 'ok').slice(0, 3).map((h, i) => (
                    <span key={i} className="text-[9px] px-2.5 py-1 bg-[#0B0B0D]/80 backdrop-blur-sm text-[#D4AF37] font-semibold border border-[#D4AF37]/20 tracking-wider">{h.name}: {h.result}</span>
                  ))}
                </div>
              </div>
            </div>
            {/* Thumbnail strip */}
            {galleryPhotos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="dog-thumbnails">
                {galleryPhotos.map((img, i) => (
                  <div
                    key={img}
                    onClick={() => setActiveThumb(activeThumb === i ? -1 : i)}
                    data-testid={`dog-thumb-${i}`}
                    className={`aspect-square bg-[#1A1A1D] border overflow-hidden cursor-pointer transition-all gallery-wow ${
                      activeThumb === i ? 'border-[#D4AF37]' : 'border-[#2A2A2D] hover:border-[#D4AF37]/40'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${dog.name} galeria ${i + 1}`}
                      className="w-full h-full object-cover object-center"
                      onError={() => setFailedImages(prev => new Set([...prev, img]))}
                    />
                    <div className="wow-overlay" />
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] overflow-hidden">
              {/* Name */}
              <div className="px-6 md:px-8 pt-6 md:pt-8 pb-6 border-b border-[#2A2A2D]">
                {dog.titles && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {dog.titles.split(",").map((t, i) => (
                      <span key={i} className="text-[10px] px-2.5 py-1 bg-[#D4AF37]/10 text-[#D4AF37] font-semibold tracking-wider border border-[#D4AF37]/20">{t.trim()}</span>
                    ))}
                  </div>
                )}
                <h1 data-testid="dog-name" className="font-serif text-[clamp(1.8rem,3vw,2.5rem)] text-[#F5F5F5] leading-tight">{dog.name}</h1>
                <p className="text-[#B8B8B8]/60 text-sm mt-2">{dog.registered_name}</p>
                <p className="text-[#B8B8B8]/40 text-xs mt-1 flex items-center gap-2 flex-wrap">
                  <span>{dog.breed || 'Owczarek niemiecki'}</span>
                  {dog.birth_date && <><span className="text-[#2A2A2D]">·</span><span>ur. {dog.birth_date}</span></>}
                </p>
                {/* FCI badge */}
                <div className="mt-4 flex gap-2">
                  <span className="text-[9px] px-2.5 py-1 border border-[#D4AF37]/20 text-[#C8A96A] tracking-wider">FCI</span>
                  <span className="text-[9px] px-2.5 py-1 border border-[#D4AF37]/20 text-[#C8A96A] tracking-wider">ZKwP</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 divide-x divide-[#2A2A2D]">
                <StatBadge icon={Calendar} label="Data urodzenia" value={dog.birth_date} />
                <StatBadge icon={Palette} label="Umaszczenie" value={dog.color} />
                <StatBadge icon={Fingerprint} label="Nr PKR" value={dog.pkr} />
                <StatBadge icon={Dna} label="Chip" value={dog.chip} />
              </div>

              {/* Parents */}
              {dog.pedigree_tree && (
                <div className="px-6 md:px-8 py-5 border-t border-[#2A2A2D]">
                  <h3 className="text-[10px] tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">RODZICE</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3"><Crown className="w-4 h-4 text-[#C8A96A]" /><span className="text-xs text-[#B8B8B8]/40 w-12">Ojciec</span><span className="text-sm text-[#F5F5F5]">{dog.pedigree_tree.father?.name || "—"}</span></div>
                    <div className="flex items-center gap-3"><Heart className="w-4 h-4 text-[#C8A96A]" /><span className="text-xs text-[#B8B8B8]/40 w-12">Matka</span><span className="text-sm text-[#F5F5F5]">{dog.pedigree_tree.mother?.name || "—"}</span></div>
                  </div>
                </div>
              )}

              {/* Breeder / Owner */}
              <div className="grid grid-cols-2 divide-x divide-[#2A2A2D] border-t border-[#2A2A2D]">
                <div className="px-6 py-4"><p className="text-[10px] tracking-[0.15em] text-[#D4AF37] mb-1">HODOWCA</p><p className="text-sm text-[#B8B8B8] flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#B8B8B8]/30" />{dog.breeder || "—"}</p></div>
                <div className="px-6 py-4"><p className="text-[10px] tracking-[0.15em] text-[#D4AF37] mb-1">WLASCICIEL</p><p className="text-sm text-[#B8B8B8] flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#B8B8B8]/30" />{dog.owner || "—"}</p></div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ═══ DESCRIPTION ═══ */}
        {dog.description && (
          <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-6 md:p-8 mb-12">
            <h3 className="text-[10px] tracking-[0.2em] text-[#D4AF37] mb-4 font-medium">O {dog.name.toUpperCase()}</h3>
            <p className="text-[#B8B8B8] text-sm leading-relaxed max-w-3xl">{dog.description}</p>
          </div>
        )}

        {/* ═══ TABS ═══ */}
        <div className="bg-[#1A1A1D] border border-[#2A2A2D] overflow-hidden">
          <div className="border-b border-[#2A2A2D] overflow-x-auto">
            <div className="flex min-w-max">
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`flex items-center gap-2 px-6 py-4 text-[11px] tracking-[0.12em] uppercase transition-all border-b-2 ${
                    tab === t.key ? "border-[#D4AF37] text-[#D4AF37] bg-[#141416]" : "border-transparent text-[#B8B8B8]/40 hover:text-[#B8B8B8]"
                  }`}
                ><t.icon className="w-4 h-4" />{t.label}</button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="p-6 md:p-8">

              {tab === "rodowod" && (
                <div data-testid="tab-content-rodowod">
                  <PedigreeTree pedigree={dog.pedigree_tree} />
                </div>
              )}

              {tab === "info" && (
                <div data-testid="tab-content-info" className="max-w-3xl">
                  <p className="text-[#B8B8B8] leading-relaxed text-sm">{dog.description || "Opis w przygotowaniu."}</p>
                  {dog.fci && <div className="mt-6 p-4 bg-[#141416] border border-[#2A2A2D]"><p className="text-[10px] tracking-[0.15em] text-[#D4AF37] mb-1">KLASYFIKACJA FCI</p><p className="text-[#B8B8B8] text-sm">{dog.fci}</p></div>}
                </div>
              )}

              {tab === "zdrowie" && (
                <div data-testid="tab-content-zdrowie">
                  {dog.health?.length > 0 ? (
                    <div className="max-w-2xl space-y-0">
                      {dog.health.map((h, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                          className="flex items-center justify-between py-4 border-b border-[#2A2A2D] last:border-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 ${h.status === 'ok' ? 'bg-[#D4AF37]' : 'bg-[#B8B8B8]/30'}`} />
                            <span className="text-sm text-[#B8B8B8]">{h.name}</span>
                          </div>
                          <span className="text-sm font-medium text-[#D4AF37]">{h.result}</span>
                        </motion.div>
                      ))}
                    </div>
                  ) : <p className="text-[#B8B8B8]/30 text-sm">Badania w trakcie</p>}
                </div>
              )}

              {tab === "szkolenie" && (
                <div data-testid="tab-content-szkolenie">
                  {dog.training?.length > 0 ? (
                    <div className="max-w-2xl">
                      {dog.training.map((t, i) => (
                        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.06 }}
                          className="flex items-center justify-between py-4 border-b border-[#2A2A2D] last:border-0"
                        >
                          <div className="flex items-center gap-3"><GraduationCap className="w-4 h-4 text-[#C8A96A]" /><span className="text-sm text-[#B8B8B8]">{t.name}</span></div>
                          <div className="text-right"><span className="text-sm text-[#D4AF37] font-medium">{t.result}</span>{t.date && <span className="text-xs text-[#B8B8B8]/30 block mt-0.5">{t.date}</span>}</div>
                        </motion.div>
                      ))}
                    </div>
                  ) : <p className="text-[#B8B8B8]/30 text-sm">Brak danych</p>}
                </div>
              )}

              {tab === "wystawy" && (
                <div data-testid="tab-content-wystawy">
                  {dog.shows?.length > 0 ? (
                    <div className="overflow-x-auto -mx-2 px-2">
                      <table className="w-full min-w-[700px] text-left">
                        <thead>
                          <tr className="border-b border-[#D4AF37]/20">
                            {["DATA", "WYSTAWA", "ORG.", "KLASA", "LOKATA / MEDAL", "OCENA", "TYTUŁY / WYRÓŻNIENIA"].map(h => (
                              <th key={h} className="text-[9px] tracking-[0.15em] text-[#D4AF37] font-medium py-3 px-2 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dog.shows.map((s, i) => (
                            <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                              className="border-b border-[#2A2A2D] last:border-0 hover:bg-[#141416]/50 transition-colors"
                            >
                              <td className="text-xs text-[#B8B8B8] py-3 px-2 whitespace-nowrap">{s.date}</td>
                              <td className="text-xs text-[#F5F5F5] py-3 px-2 font-medium">{s.show}</td>
                              <td className="text-xs text-[#B8B8B8]/60 py-3 px-2">{s.organizer}</td>
                              <td className="text-xs text-[#B8B8B8] py-3 px-2">{s.class || "—"}</td>
                              <td className="text-xs text-[#F5F5F5] py-3 px-2 font-medium">{s.place}</td>
                              <td className="text-xs text-[#D4AF37] py-3 px-2 font-semibold">{s.rating || "—"}</td>
                              <td className="text-xs text-[#C8A96A] py-3 px-2">{s.titles || ""}</td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-[#B8B8B8]/30 text-sm">Brak danych</p>}
                </div>
              )}

              {tab === "certyfikaty" && (
                <div data-testid="tab-content-certyfikaty">
                  {dog.certificates?.length > 0 ? (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {dog.certificates.map((c, i) => (
                        <motion.a
                          key={i}
                          href={c.url || "#"}
                          target={c.url ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.08 }}
                          className={`bg-[#141416] border border-[#2A2A2D] p-5 transition-all group ${c.url ? 'hover:border-[#D4AF37]/40 cursor-pointer hover:bg-[#1A1A1D]' : 'opacity-60'}`}
                          data-testid={`cert-link-${i}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <FileText className="w-5 h-5 text-[#C8A96A]" />
                            {c.url && (
                              <span className="text-[8px] tracking-[0.2em] text-[#D4AF37]/50 group-hover:text-[#D4AF37] transition-colors">OTWORZ PDF</span>
                            )}
                          </div>
                          <p className="text-sm text-[#F5F5F5] group-hover:text-[#D4AF37] transition-colors">{c.name}</p>
                          {c.type && <p className="text-[10px] text-[#B8B8B8]/40 mt-1 tracking-wider uppercase">{c.type}</p>}
                        </motion.a>
                      ))}
                    </div>
                  ) : <p className="text-[#B8B8B8]/30 text-sm">Brak certyfikatów</p>}
                </div>
              )}

              {tab === "galeria" && (
                <div data-testid="tab-content-galeria">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {allGalleryPhotos.map((img, idx) => (
                      <motion.div key={img} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: (idx % 8) * 0.03 }}
                        className="relative aspect-square overflow-hidden bg-[#0B0B0D] cursor-pointer group"
                        onClick={() => setLightboxImg(img)}
                      >
                        <img src={img} alt={`${dog.name} galeria ${idx + 1}`} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-105" onError={() => setFailedImages(prev => new Set([...prev, img]))} />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      </motion.div>
                    ))}
                  </div>
                  {allGalleryPhotos.length === 0 && <p className="text-[#B8B8B8]/30 text-sm text-center py-8">Zdjęcia w przygotowaniu</p>}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ═══ BREEDING PLANS CTA ═══ */}
        <div className="mt-12 bg-[#1A1A1D] border border-[#2A2A2D] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-[10px] tracking-[0.2em] text-[#D4AF37] mb-2 font-medium">PLANY HODOWLANE</h3>
            <p className="text-[#B8B8B8] text-sm">Zainteresowany potomstwem {dog.name}? Skontaktuj sie z nami.</p>
          </div>
          <Link to="/kontakt" className="px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all flex-shrink-0">
            ZAPYTAJ O SZCZENIETA
          </Link>
        </div>
      </div>

      {/* Lightbox with prev/next arrows */}
      <AnimatePresence>
        {lightboxImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-[#0B0B0D]/95 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}
          >
            <button onClick={() => setLightboxImg(null)} className="absolute top-6 right-6 z-50 w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors" data-testid="dog-lightbox-close" aria-label="Zamknij">
              <X className="w-5 h-5" />
            </button>
            {(dog.gallery || []).filter(Boolean).filter(img => !failedImages.has(img)).length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); dogLbNav("prev"); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] transition-colors" data-testid="dog-lightbox-prev" aria-label="Poprzednie"><ChevronLeft className="w-6 h-6" /></button>
                <button onClick={(e) => { e.stopPropagation(); dogLbNav("next"); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] transition-colors" data-testid="dog-lightbox-next" aria-label="Następne"><ChevronRight className="w-6 h-6" /></button>
              </>
            )}
            <motion.img key={lightboxImg} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              src={lightboxImg} alt="" className="max-w-[92vw] max-h-[88vh] object-contain" onClick={e => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KennelDogProfile;
